import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type Chatbot } from 'entities/Chatbot'
import { Segment } from 'entities/Segment/model/Segment'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { SearchTypesEnum } from 'widgets/SearchDropdown'

export class ChatbotEnrollment {
  private _enrollmentStore: EnrollmentStore
  public enrollmentFilters = EnrollmentFilters.Empty()
  private _disposeIsShowContactsAlert: IReactionDisposer | null = null

  constructor(private _chatbot: Chatbot, private _entities: IEntitiesStore) {
    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              withCustomFields: true,
              workflowSource: 'chatbot',
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this._entities
    )

    makeAutoObservable(this)

    this.reactionIsShowContactsAlert()
  }

  reactionIsShowContactsAlert = () => {
    this._disposeIsShowContactsAlert?.()
    this._disposeIsShowContactsAlert = reaction(
      () => [this._enrollmentStore.isContactReEnroll, this.enrollmentFilters.filters],
      () => this._enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    )
  }

  get enrollmentStore() {
    return this._enrollmentStore
  }

  initFilters = () => this._enrollmentStore.filterEditorStore.initFilters()

  editEnrollment = () => {
    const filters = this.enrollmentFilters.clone()

    const cancelFilters = () => Promise.resolve(true)
    const confirmFilters = async (newFilters: EnrollmentFilters) => {
      this.enrollmentFilters = newFilters

      this._chatbot.filters.syncTagIds(this.enrollmentFilters.tags)
      this._chatbot.filters.syncContactIds(this.enrollmentFilters.contacts)
      this._chatbot.filters.syncSegmentIds(this.enrollmentFilters.segments)
      this._chatbot.filters.syncAdvanced(this.enrollmentFilters.filters)

      return true
    }

    this._enrollmentStore.openModal({
      title: 'Contacts',
      allowEmptyCount: true,
      filters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
    })
  }

  syncFilters = async () => {
    const [tags, contacts] = await Promise.all([
      TagsApi.getTagsById(this._chatbot.filters.tagIds).then((tags) =>
        tags.map((tag) => new Tag(tag))
      ),
      this._entities.contactsStore.getByIds(this._chatbot.filters.contactIds),
    ])

    const segments = this._chatbot.filters.segmentIds
      .map(this._entities.segmentsStore.getItem)
      .filter(Boolean) as Segment[]

    const filters = this._chatbot.filters.advanced.map(
      (filters) =>
        new Segment({ id: -1, organization_id: 0, name: '', filters, is_favorite: false })
    )

    this.enrollmentFilters.sync({ tags, segments, contacts, filters })
    this._enrollmentStore.setContactReEnrollAlert(this.enrollmentFilters.filters)
    this._enrollmentStore.setIsContactReEnroll(this._chatbot.settings.allowReEntering)
  }

  dispose = () => {
    this._disposeIsShowContactsAlert?.()
  }
}
